import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateTaskRelatedQueries } from "@/utils/cacheInvalidation";
import { requestURLs } from "../../Environment";
import { z } from "zod";
import {
  AxiosResponseAndError,
  ResponseType,
} from "@/api/types/axiosResponseType";
import { createTaskSchema } from "@backend/src/schemas/taskSchema";
import ApiRequest from "../ApiRequest";
import {
  TaskDependenciesEnumValue,
  TaskStatusEnumValue,
} from "@backend/src/schemas/enums";
import { UserType } from "../query/useCurrentUserQuery";
import { UserOrganisationType } from "../query/useOrganisationDetailsQuery";
import { RiskType } from "./useAddRIskMutation";

export type Task = {
  taskId: string;
  projectId: string;
  taskName: string;
  taskDescription: string;
  startDate: Date;
  duration: number;
  completionPecentage: string | undefined;
  status: keyof typeof TaskStatusEnumValue;
  milestoneIndicator: boolean;
  createdByUserId: string;
  updatedByUserId: string;
  dueDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
  parentTaskId: null | string;
  deletedAt?: Date | string | null;
  dependencies: Dependencies[];
  comments: Comments[];
  documentAttachments: DocumentAttachment[];
  endDate: Date;
  flag: string;
  delay: number | null;
  tpi: number | null;
  plannedProgression: number | null;
  subtasks: Task[];
  ganttColor?:string,
  taskRisks: TaskRisk[];
  deliverableTasks?: {
    deliverableTaskId?: string;
    deliverableId: string;
    taskId: string;
    deliverable: {
      deliverableId: string;
      deliverableNameId: string;
      name: string;
      status: string;
    };
  }[];
  assignedUsers: {
    assginedToUserId: string;
    taskAssignUsersId: string;
    user: UserOrganisationType["user"];
    workingHours: number;
  }[];
  kanbanColumnId: string | null;
  histories: History[];
  taskNotes:TaskNotes[]
};
export interface History {
  historyId: string;
  referenceId: string;
  type: string;
  data: HistoryData;
  message: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  createdByUser: UserOrganisationType["user"];
}
export interface TaskRisk {
  taskRiskId: string;
  taskId: string;
  riskId: string;
  createdAt: string;
  task: Task;
  risk: RiskType;
}

export interface HistoryData {
  newValue: string;
  oldValue: string | null;
}

export interface Dependencies {
  taskDependenciesId: string;
  dependentTaskId: string;
  dependentType: keyof typeof TaskDependenciesEnumValue;
  dependendentOnTaskId: string;
  createdAt: Date;
  updatedAt: Date;
  dependentOnTask: Task;
}

export interface DependentOnTask {
  taskId: string;
  projectId: string;
  taskName: string;
  taskDescription: string;
  startDate: Date;
  duration: number;
  completionPecentage: null;
  status: string;
  milestoneIndicator: boolean;
  dueDate: null;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: Date;
  updatedAt: Date;
  parentTaskId: string;
  endDate: Date;
  flag: string;
}
export interface Comments {
  childComments: Comments[];
  commentId: string;
  taskId: string;
  commentText: string;
  commentByUserId: string;
  createdAt: Date;
  updatedAt: Date;
  commentByUser: UserType;
}
export interface DocumentAttachment {
  attachmentId: string;
  name: string;
  url: string;
  taskId: string;
  label?: string;
  createdAt: Date;
  updateAt: Date;
}

export interface TaskNotes {
  noteId: string
  note: string
  createdByUserId: string
  updatedByUserId?: string | null
  taskId: string
  createdAt: string
  updatedAt: string
  createdByUser: UserOrganisationType["user"];
  updatedByUser: UserOrganisationType["user"];
}

type CreateTaskResponseType = ResponseType<Task>;

function useCreateTaskMutation(projectId?: string, taskId?: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    AxiosResponseAndError<CreateTaskResponseType>["response"],
    AxiosResponseAndError<CreateTaskResponseType>["error"],
    z.infer<typeof createTaskSchema> & { tempId?: string }
  >({
    mutationFn: (data) =>
      ApiRequest.post<CreateTaskResponseType>(
        `${requestURLs.task}${projectId}/${data.tempId ? data.tempId : taskId??''}`,
        data
      ),
    onSuccess: (res) => {
      const createdTaskId = res?.data?.data?.taskId;
      const targetProjectId = res?.data?.data?.projectId || projectId;
      void invalidateTaskRelatedQueries(queryClient, {
        taskId: createdTaskId,
        projectId: targetProjectId,
      });
    },
  });

  return mutation;
}

export default useCreateTaskMutation;
